import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  Autocomplete,
  ButtonGroup
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as ProcessIcon
} from '@mui/icons-material';
import { schemaApiService } from '../../services/schemaApiService';
import { urlService } from '../../services/urlService';

interface SchemaProcessorProps {
  onProcessComplete: () => void;
}

const SchemaProcessor: React.FC<SchemaProcessorProps> = ({ onProcessComplete }) => {
  const selectedWebsite = useSelector((state: RootState) => state.website.selectedWebsite);
  const [singleUrl, setSingleUrl] = useState('');
  const [batchPriority, setBatchPriority] = useState<string>('');
  const [batchLimit, setBatchLimit] = useState(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [availableUrls, setAvailableUrls] = useState<string[]>([]);
  const [loadingUrls, setLoadingUrls] = useState(false);

  useEffect(() => {
    if (selectedWebsite) {
      loadAvailableUrls();
    }
  }, [selectedWebsite]);

  const loadAvailableUrls = async () => {
    if (!selectedWebsite?.website_id) {
      console.log('No website selected');
      return;
    }
    
    try {
      setLoadingUrls(true);
      console.log('Loading URLs for website:', selectedWebsite.website_id);
      
      const { urls, error } = await urlService.getUrlsByWebsite(selectedWebsite.website_id);

      if (error) {
        console.error('Error fetching URLs:', error);
        setError(`Error loading URLs: ${error}`);
        return;
      }

      console.log('Found URLs:', urls?.length || 0);
      const urlList = urls.map(u => u.full_url).filter(Boolean);
      setAvailableUrls(urlList);
      
    } catch (err) {
      console.error('Error loading URLs:', err);
      setError('Failed to load URLs');
    } finally {
      setLoadingUrls(false);
    }
  };

  const handleSingleUrlSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      if (!singleUrl.trim()) {
        setError('Please enter a URL');
        return;
      }

      const result = await schemaApiService.generateSingleSchema(singleUrl);
      
      if (result.success) {
        setSuccess(`Schema generated successfully for ${singleUrl}`);
        setSingleUrl('');
        onProcessComplete();
      } else {
        setError('Failed to generate schema');
      }
    } catch (err: any) {
      setError(err.message || 'Error generating schema');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const params: any = { 
        limit: batchLimit,
        websiteId: selectedWebsite?.website_id || 'newyork.co.uk'
      };
      if (batchPriority) {
        params.priority = batchPriority;
      }

      const result = await schemaApiService.startBatchProcessing(params);
      
      if (result.success) {
        if (result.batchId) {
          // Batch API submission successful
          const resultAny = result as any;
          let message = `🎉 Submitted ${resultAny.requestCount || result.queued} URLs to Claude Batch API! `;
          
          if (resultAny.skippedCount && resultAny.skippedCount > 0) {
            message += `\n⚠️ ${resultAny.skippedCount} URL${resultAny.skippedCount > 1 ? 's' : ''} skipped (not suitable for schemas). `;
          }
          
          message += `\nBatch ID: ${result.batchId}. `;
          message += `Status: ${result.status}. `;
          
          if (result.costSavings) {
            message += `Cost Savings: ${result.costSavings}. `;
          }
          
          if (result.estimatedCompletion) {
            message += `Estimated Completion: ${result.estimatedCompletion}`;
          }
          
          setSuccess(message);
        } else if (result.demoSchema) {
          setSuccess(`Generated demo schema for first URL! ${result.queued} URLs ready to process.`);
        } else {
          setSuccess(`${result.message || `Added ${result.queued} URLs to processing queue`}`);
        }
        onProcessComplete();
      } else {
        setError(result.error || 'Failed to start batch processing');
      }
    } catch (err: any) {
      setError(err.message || 'Error starting batch processing');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Single URL Processing */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Process Single URL
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Generate schema for a specific URL immediately
            </Typography>
            
            <Autocomplete
              fullWidth
              options={availableUrls}
              value={singleUrl}
              onChange={(event, newValue) => setSingleUrl(newValue || '')}
              onInputChange={(event, newInputValue) => setSingleUrl(newInputValue)}
              loading={loadingUrls}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="URL"
                  placeholder="Start typing or select a URL..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUrls ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ mb: 2 }}
            />
            
            <Button
              fullWidth
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
              onClick={handleSingleUrlSubmit}
              disabled={loading || !singleUrl.trim()}
            >
              Generate Schema
            </Button>
          </Paper>
        </Grid>

        {/* Batch Processing */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Batch Processing
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Process multiple URLs at once (test with 5, then scale up!)
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Priority Filter</InputLabel>
              <Select
                value={batchPriority}
                onChange={(e) => setBatchPriority(e.target.value)}
                label="Priority Filter"
                disabled={loading}
              >
                <MenuItem value="">All URLs</MenuItem>
                <MenuItem value="V">V - Very Important (Revenue)</MenuItem>
                <MenuItem value="C">C - Commercial Intent</MenuItem>
                <MenuItem value="S">S - SEO Value</MenuItem>
              </Select>
            </FormControl>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Quick presets:
              </Typography>
              <ButtonGroup size="small" sx={{ mb: 2 }}>
                <Button onClick={() => setBatchLimit(5)} variant={batchLimit === 5 ? "contained" : "outlined"}>5</Button>
                <Button onClick={() => setBatchLimit(10)} variant={batchLimit === 10 ? "contained" : "outlined"}>10</Button>
                <Button onClick={() => setBatchLimit(25)} variant={batchLimit === 25 ? "contained" : "outlined"}>25</Button>
                <Button onClick={() => setBatchLimit(50)} variant={batchLimit === 50 ? "contained" : "outlined"}>50</Button>
                <Button onClick={() => setBatchLimit(100)} variant={batchLimit === 100 ? "contained" : "outlined"}>100</Button>
              </ButtonGroup>
              <TextField
                fullWidth
                type="number"
                label="Number of URLs to Process"
                value={batchLimit}
                onChange={(e) => setBatchLimit(parseInt(e.target.value) || 5)}
                disabled={loading}
                helperText="Start small (5-10) to test, then scale up to 100+"
                inputProps={{ min: 1, max: 500 }}
              />
            </Box>
            
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={loading ? <CircularProgress size={20} /> : <ProcessIcon />}
              onClick={handleBatchSubmit}
              disabled={loading}
            >
              Process {batchLimit} URLs
            </Button>
          </Paper>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Information */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Processing Information
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="subtitle2" gutterBottom>
              Priority Levels
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • V - Revenue-critical pages (tickets, tours, hotels)<br />
              • C - Commercial intent pages (restaurants, shopping)<br />
              • S - Informational pages (neighborhoods, guides)<br />
              • Unclassified - All remaining content
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="subtitle2" gutterBottom>
              Processing Speed
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Batch API: ~12 pages/hour (50% cost savings)<br />
              • Single URL: Immediate processing<br />
              • Priority order: V → C → S → Unclassified
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="subtitle2" gutterBottom>
              Cost Estimates
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • ~$0.01 per page using Claude Sonnet<br />
              • Batch API reduces costs by 50%<br />
              • Full site (600 pages): ~$6 total
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SchemaProcessor;